import React from 'react';
import { graphql, Link } from 'gatsby';
import styled from '@emotion/styled';
import newsBg from 'src/components/news/newsBg.jpg';
import Layout from 'src/layouts/default/default';
import { iPadBreakPoint, mobileBreakPoint } from 'src/utils/ResponsiveDesign';
import RelatedNews from 'src/components/RelatedNews';
import RelatedCases from 'src/components/RelatedCases';

const Container = styled.div`
  width: 90%;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const LeftWrap = styled.div`
  width: 90%;
  margin: auto;
`;

const LeftContent = styled.div({
  width: '59%',
  borderRight: '1px solid #c6c7c8',
  [iPadBreakPoint]: {
    width: '100%',
  },
  [mobileBreakPoint]: {
    width: '100%',
  },
  h2: {
    width: '100%',
    color: '#004098',
    fontSize: '20px',
    marginBottom: '10px',
  },
  h3: {
    width: '100%',
    color: '#575757',
    fontSize: '18px',
    fontFamily: '微软雅黑',
    marginBottom: '10px',
    marginTop: '10px',
  },
  p: {
    color: '#333',
    textIndent: '2em',
    letterSpacing: '2px',
    lineHeight: '1.5',
    marginBottom: '20px',
  },
  img: {
    width: '100%',
    marginBottom: '10px',
  },
  figcaption: {
    textAlign: 'center',
    margin: '10px 0 40px',
  },
  '.fication': {
    textAlign: 'center',
    fontSize: '13px',
    margin: '10px 0 20px',
  },
  '.desc': {
    fontSize: '13px',
    margin: '10px 0 20px',
    lineHeight: '1.5',
  },
  '.secondTitle': {
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  '.cases': {
    p: {
      color: '#333',
      textIndent: '0',
      letterSpacing: '2px',
      lineHeight: '1.5',
      marginBottom: '10px',
    },
  },
  ul: {
    marginBottom: '20px',
  },
  li: {
    color: '#575757',
    letterSpacing: '2px',
    lineHeight: '1.5',
  },
  a: {
    color: '#005AB5',
  },
});

const Title = styled.h1(props => ({
  color: '#004098',
  fontSize: '25px',
  display: 'flex',
  justifyContent: 'center',
  marginTop: props.subTitle ? '7px' : '0',
}));

const Date = styled.div({
  width: '100%',
  background: '#f5f5f5',
  height: '35px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '20px',
  marginBottom: '20px',
});

const ClickAmount = styled.div({
  color: '#575757',
});

const Time = styled.div({
  color: '#575757',
  marginLeft: '20px',
});

const Banner = styled.img({
  width: '100%',
  marginBottom: '20px',
});

const RightContent = styled.div`
  width: 40.5%;
  ${iPadBreakPoint} {
    width: 100%;
  }
  ${mobileBreakPoint} {
    width: 100%;
  }
`;

const RightWrap = styled.div({
  background: '#f5f5f5',
  width: '90%',
  margin: 'auto',
  paddingBottom: '20px',
});

const BreadcrumbItem = styled(Link)({
  color: '#c6c7c8',
  '&:hover': {
    color: '#004095',
  },
});

const BreadcrumbItemCurrent = styled('span')({
  color: '#004095',
});

const Separator = styled('span')({
  color: '#c6c7c8',
  margin: '0 14px',
});

const BreadcrumbContainer = styled('div')({
  width: '100%',
  padding: '10px',
  borderBottom: '1px solid #c6c7c8',
  marginBottom: '40px',
});

const ArticleContext = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
});

const ItemLink = styled(Link)({
  // margin: '30px 0 60px',
  // display: 'block',
});

const DisablePage = styled('div')({
  pointerEvents: 'none',
  cursor: 'no-drop',
  opacity: '0.3',
});

const Breadcrumb = ({ pathToRoot, current, separator = '>>' }) => {
  const paths = pathToRoot.reduce((res, cur) => {
    res.push(
      <BreadcrumbItem key={cur.text} to={cur.href}>
        {cur.text}
      </BreadcrumbItem>
    );
    res.push(<Separator key={`${cur.text}-spe`}>{separator}</Separator>);
    return res;
  }, []);
  paths.push(
    <BreadcrumbItemCurrent key={current.text}>
      {current.text}
    </BreadcrumbItemCurrent>
  );
  return <BreadcrumbContainer>{paths}</BreadcrumbContainer>;
};

export default function Template({
  // eslint-disable-next-line react/prop-types
  data, // this prop will be injected by the GraphQL query below.
}) {
  const pathToRoot = [{ text: '首页', href: '/' }];
  const pathCurrent = { text: '高仙资讯' };
  let isBack = true;
  let isNext = true;
  const currentPage = data.wordpressPost;
  const allPages = data.allWordpressPost.edges;
  let nextJumpArticle = '';
  let prevJumpArticle = '';
  // eslint-disable-next-line
  for (let i = 0; i < allPages.length; i++) {
    if (currentPage.slug === allPages[i].news.slug) {
      if (i < allPages.length - 1) {
        nextJumpArticle = allPages[i + 1].news.slug;
      } else {
        isNext = false;
      }
    }
  }
  // eslint-disable-next-line
  for (let i = 0; i < allPages.length; i++) {
    if (currentPage.slug === allPages[i].news.slug) {
      if (i > 0) {
        prevJumpArticle = allPages[i - 1].news.slug;
      } else {
        isBack = false;
      }
    }
  }
  return (
    <Layout
      title={currentPage.title}
      keywords="无人驾驶环卫车_无人扫地车_无人清扫车_安防巡检机器人_SLAM_激光导航-高仙机器人"
    >
      <Banner src={newsBg} alt="" />
      <Container>
        <Breadcrumb pathToRoot={pathToRoot} current={pathCurrent} />
        <LeftContent>
          <LeftWrap>
            <Title>
              {currentPage.title.split(',')[0] || currentPage.title}
            </Title>
            <Title subTitle>{currentPage.title.split(',')[1] || ''}</Title>
            <Date>
              <ClickAmount>点击量: 1020</ClickAmount>
              <Time>日期: {currentPage.date}</Time>
            </Date>
            <div dangerouslySetInnerHTML={{ __html: currentPage.content }} />
            <ArticleContext>
              {(isBack && (
                <ItemLink to={`/news/${prevJumpArticle}`}>
                  &lt;&lt;上一篇
                </ItemLink>
              )) || <DisablePage>&lt;&lt;上一篇</DisablePage>}
              {(isNext && (
                <ItemLink to={`/news/${nextJumpArticle}`}>
                  下一篇&gt;&gt;
                </ItemLink>
              )) || <DisablePage>下一篇&gt;&gt;</DisablePage>}
            </ArticleContext>
          </LeftWrap>
        </LeftContent>
        <RightContent>
          <RightWrap>
            {currentPage.categories[0].name === '新闻' && <RelatedNews />}
            {currentPage.categories[0].name === '案例' && <RelatedCases />}
          </RightWrap>
        </RightContent>
      </Container>
    </Layout>
  );
}

export const pageQuery = graphql`
  query($id: String!) {
    wordpressPost(id: { eq: $id }) {
      title
      content
      slug
      date(formatString: "YYYY-MM-DD")
      categories {
        name
      }
    }
    allWordpressPost(
      sort: { fields: [date], order: DESC }
      filter: { categories: { elemMatch: { name: { in: ["案例", "新闻"] } } } }
    ) {
      edges {
        news: node {
          id
          title
          excerpt
          slug
          content
          date(formatString: "YYYY.MM.DD")
          categories {
            name
          }
          cover: featured_media {
            image: localFile {
              childImageSharp {
                fluid(maxHeight: 500, quality: 50) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
